import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const InfluencerPortal = ({ currentUserId }) => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [bidCounts, setBidCounts] = useState({});
  const [ratings, setRatings] = useState([]);
  const [averageRating, setAverageRating] = useState(0);

  // Format date in South African Standard Time (SAST)
  const formatSASTDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    
    // Format options for SAST display
    const options = {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: 'Africa/Johannesburg'
    };
    
    const formattedDate = date.toLocaleString('en-ZA', options);
    // Format: "31 Jan 2026 • 16:28 (SAST)"
    const parts = formattedDate.split(', ');
    if (parts.length === 2) {
      return `${parts[0]} • ${parts[1]} (SAST)`;
    }
    return `${formattedDate} (SAST)`;
  };

  useEffect(() => {
    fetchCampaigns();
    fetchRatings();
  }, [currentUserId]);

  const fetchCampaigns = async () => {
    try {
      const response = await axios.get(`${API}/campaigns?status=open`);
      const campaignsData = response.data;
      
      // Fetch listing details for each campaign
      const campaignsWithListings = await Promise.all(
        campaignsData.map(async (campaign) => {
          try {
            const listingResponse = await axios.get(`${API}/listings/${campaign.listingId}`);
            return { ...campaign, listing: listingResponse.data };
          } catch (error) {
            console.error(`Failed to fetch listing for campaign ${campaign.id}:`, error);
            return { ...campaign, listing: null };
          }
        })
      );
      
      setCampaigns(campaignsWithListings);

      // Fetch aggregated bid counts for every open campaign in one request
      const ids = campaignsWithListings.map(c => c.id).filter(Boolean);
      if (ids.length > 0) {
        try {
          const countsRes = await axios.get(
            `${API}/bids/counts?campaignIds=${encodeURIComponent(ids.join(','))}`
          );
          setBidCounts(countsRes.data?.counts || {});
        } catch (err) {
          console.error('Failed to fetch bid counts:', err);
          setBidCounts({});
        }
      }
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      toast.error('Failed to load campaigns');
    }
  };

  const fetchRatings = async () => {
    try {
      const response = await axios.get(`${API}/ratings/influencer/${currentUserId}`);
      setRatings(response.data);
      
      if (response.data.length > 0) {
        const total = response.data.reduce((sum, r) => sum + r.rating, 0);
        setAverageRating(total / response.data.length);
      }
    } catch (error) {
      console.error('Error fetching ratings:', error);
    }
  };

  const handleSubmitBid = (campaign) => {
    navigate('/submit-bid', { state: { campaign } });
  };

  const renderStars = (rating) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;
    const stars = [];

    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(<i key={i} className="fas fa-star text-yellow-500"></i>);
      } else if (i === fullStars && hasHalfStar) {
        stars.push(<i key={i} className="fas fa-star-half-alt text-yellow-500"></i>);
      } else {
        stars.push(<i key={i} className="far fa-star text-gray-300"></i>);
      }
    }

    return <span className="text-sm">{stars}</span>;
  };

  const formatZAR = (amount) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  return (
    <main className="max-w-7xl mx-auto px-4 py-8">
      <h2 className="text-3xl font-bold mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
        Influencer Portal
      </h2>

      <Card className="mb-8 bg-gradient-to-r from-purple-50 to-pink-50">
        <CardHeader>
          <CardTitle className="flex items-center text-purple-700">
            <i className="fas fa-user-circle mr-2 text-2xl"></i> Your Influencer Profile
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <p className="text-sm text-gray-600 mb-1">Average Rating</p>
              <div className="flex items-center gap-3">
                {renderStars(averageRating)}
                <span className="font-bold text-2xl text-gray-800">{averageRating.toFixed(1)}/5.0</span>
                <span className="text-gray-600">({ratings.length} review{ratings.length !== 1 ? 's' : ''})</span>
              </div>
            </div>
            
            {ratings.length > 0 && (
              <div>
                <p className="font-semibold text-gray-700 mb-2">Recent Reviews:</p>
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {ratings.slice(0, 5).map(rating => (
                    <div key={rating.id} className="bg-white p-3 rounded-lg border">
                      <div className="flex items-center gap-2 mb-1">
                        {renderStars(rating.rating)}
                        <span className="text-xs text-gray-500">{new Date(rating.createdAt).toLocaleDateString()}</span>
                      </div>
                      <p className="text-sm text-gray-700">{rating.review}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <h3 className="text-2xl font-bold mb-4 border-t pt-6">Open Campaigns for Collaboration</h3>
      
      {/* Payment Notice */}
      <Card className="mb-6 bg-blue-50 border-blue-200">
        <CardContent className="pt-4">
          <div className="flex items-start gap-3">
            <i className="fas fa-info-circle text-blue-600 text-xl mt-1"></i>
            <div>
              <p className="font-semibold text-blue-900 mb-1">Payment Notice</p>
              <p className="text-sm text-blue-800">
                Flippy Homes does not process any bid payments. All payment arrangements are directly between you and the agent/property owner. 
                Please discuss and agree on payment terms before accepting any campaign work.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {campaigns.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <i className="fas fa-bullhorn text-4xl text-gray-300 mb-4"></i>
            <p className="text-gray-500">No open campaigns available at the moment.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {campaigns.map(campaign => (
            <Card key={campaign.id} className="hover:shadow-lg transition-shadow" data-testid="campaign-card">
              <CardHeader className="bg-gradient-to-r from-red-50 to-orange-50">
                <CardTitle className="text-xl text-red-700">{campaign.title}</CardTitle>
                {/* Campaign Submission Timestamp */}
                {campaign.createdAt && (
                  <p className="text-xs text-gray-500 mt-1 flex items-center gap-1" data-testid="campaign-timestamp">
                    <i className="fas fa-clock"></i>
                    <span className="font-medium">Submitted:</span> {formatSASTDate(campaign.createdAt)}
                  </p>
                )}
              </CardHeader>
              <CardContent className="pt-4">
                {/* Listing Preview Card */}
                {campaign.listing && (
                  <div className="mb-4 bg-gradient-to-r from-blue-50 to-green-50 border-2 border-blue-200 rounded-lg p-3">
                    <h4 className="font-bold text-blue-800 mb-2 text-sm flex items-center gap-2">
                      <i className="fas fa-home"></i> Property to Promote
                    </h4>
                    <div className="bg-white rounded-lg overflow-hidden shadow-sm flex gap-3">
                      {/* Property Image */}
                      <div className="w-32 h-24 flex-shrink-0">
                        {campaign.listing.images && campaign.listing.images.length > 0 ? (
                          <img 
                            src={campaign.listing.images[0]} 
                            alt={campaign.listing.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                            <span className="text-3xl">🏠</span>
                          </div>
                        )}
                      </div>
                      
                      {/* Property Details */}
                      <div className="flex-1 py-2">
                        <h5 className="font-bold text-sm mb-1">{campaign.listing.title}</h5>
                        <p className="text-blue-600 font-bold text-lg">
                          R{campaign.listing.price?.toLocaleString()}
                        </p>
                        <div className="flex gap-3 text-xs text-gray-600 mt-1">
                          <span><i className="fas fa-bed mr-1"></i>{campaign.listing.specifications?.beds || 0}</span>
                          <span><i className="fas fa-bath mr-1"></i>{campaign.listing.specifications?.baths || 0}</span>
                          <span className="capitalize">{campaign.listing.propertyType?.replace('_', ' ')}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="space-y-2 mb-4">
                  <p className="text-sm">
                    <span className="font-semibold">Platform:</span>{' '}
                    <span className="text-red-600 font-bold">{campaign.platform}</span>
                  </p>
                  <p className="text-sm">
                    <span className="font-semibold">Budget:</span> {formatZAR(campaign.budget)}
                  </p>
                  <p className="text-sm">
                    <span className="font-semibold">Min. Followers:</span> {campaign.minFollowers.toLocaleString()}
                  </p>
                  <p className="text-sm">
                    <span className="font-semibold">Duration:</span> {campaign.duration === 'day' ? '1 Day' : campaign.duration === 'week' ? '1 Week' : '1 Month'}
                  </p>
                  {/* Bid count — aggregated from /api/bids/counts */}
                  <p
                    className="text-xs text-gray-500 flex items-center gap-1 pt-1"
                    data-testid="bid-count"
                  >
                    <i className="fas fa-users"></i>
                    {bidCounts[campaign.id] > 0
                      ? `${bidCounts[campaign.id]} ${bidCounts[campaign.id] === 1 ? 'influencer applied' : 'influencers applied'}`
                      : 'No bids yet'}
                  </p>
                </div>
                
                <Button
                  className="bg-red-600 hover:bg-red-700 w-full"
                  onClick={() => handleSubmitBid(campaign)}
                  data-testid="submit-bid-btn"
                >
                  <i className="fas fa-paper-plane mr-2"></i> Submit Bid
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </main>
  );
};

export default InfluencerPortal;